import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faRepeat, faSave, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
import {
  MAIN_TAB_PAGE_LABEL,
  medPromptBrowserTitle,
} from '@medprompt/shared';
import '@medprompt/shared/src/common/modals.css';
import HelpButton from '../help/HelpButton';
import { HELP_PAGE_IDS } from '../help/helpContent';
import CommTemplateManagerModal from '../detail/tabs/CommTemplateManagerModal';
import './ConfigView.css';
import useConfigForm from './useConfigForm';
import DocumentConfigSection from './DocumentConfigSection';
import GeneratorConfigSection from './GeneratorConfigSection';

const ConfigViewController = ({ onClose, isSidebarExpanded }) => {
  const form = useConfigForm();

  useEffect(() => {
    document.title = medPromptBrowserTitle(MAIN_TAB_PAGE_LABEL.configuration);
  }, []);

  const configViewClassName = `config-view${isSidebarExpanded ? ' sidebar-expanded' : ''}`;

  const renderConfigHeader = (showActions = true) => (
    <div className="header">
      <div className="header-title">
        <div className="header-title-text">
          <div className="header-title-with-help">
            <h1 className="tab-page-title">{MAIN_TAB_PAGE_LABEL.configuration}</h1>
            <HelpButton pageId={HELP_PAGE_IDS.config} />
          </div>
        </div>
      </div>
      {showActions && (
        <div className="header-buttons">
          {form.isEditingConfig ? (
            <>
              <button
                className="header-button header-button-primary"
                onClick={form.handleSave}
                disabled={form.saving || form.documentSection.hasTitleComponentErrors}
                title={form.documentSection.hasTitleComponentErrors ? 'Fix title format errors before saving' : undefined}
              >
                {form.saving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} /> Save
                  </>
                )}
              </button>
              <button className="header-button" onClick={form.handleCancel} disabled={form.saving}>
                <FontAwesomeIcon icon={faTimes} /> Cancel
              </button>
            </>
          ) : (
            <button className="header-button" onClick={form.handleEditToggle}>
              <FontAwesomeIcon icon={faPencil} /> Edit
            </button>
          )}
        </div>
      )}
    </div>
  );

  if (form.loading) {
    return (
      <div className={configViewClassName}>
        {renderConfigHeader(false)}
        <div className="config-content">
          <div className="loading-container">
            <div className="loading-spinner">
              <div className="spinner-circle"></div>
            </div>
            <div className="loading-text">Loading configuration...</div>
          </div>
        </div>
      </div>
    );
  }

  if (form.error) {
    return (
      <div className={configViewClassName}>
        {renderConfigHeader(false)}
        <div className="config-content">
          <div className="error-container">
            <h3>Error Loading Configuration</h3>
            <p>{form.error}</p>
            <button className="button" onClick={() => form.fetchConfig()}>
              <FontAwesomeIcon icon={faRepeat} /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={configViewClassName}>
      {renderConfigHeader(true)}

      <div className="config-content">
        <DocumentConfigSection
          documentSection={form.documentSection}
          isEditingConfig={form.isEditingConfig}
          saving={form.saving}
          onOpenTemplateManager={() => form.setIsTemplateManagerModalOpen(true)}
        />
        <GeneratorConfigSection
          config={form.config}
          isEditingConfig={form.isEditingConfig}
          saving={form.saving}
          selectedTypes={form.selectedTypes}
          toggleSearchType={form.toggleSearchType}
          clinicalDepartments={form.clinicalDepartments}
          letterTypes={form.letterTypes}
          measurements={form.measurements}
          handleCodeSearch={form.handleCodeSearch}
          documentContentTypeToGuidance={form.documentContentTypeToGuidance}
          onGuidanceChange={form.handleGuidanceChange}
          onGuidanceClear={form.handleGuidanceClear}
          shouldExtractExtendedMeasurements={form.shouldExtractExtendedMeasurements}
          setShouldExtractExtendedMeasurements={form.setShouldExtractExtendedMeasurements}
          shouldBundleBloodPressureReadings={form.shouldBundleBloodPressureReadings}
          setShouldBundleBloodPressureReadings={form.setShouldBundleBloodPressureReadings}
        />
      </div>

      {form.isTemplateManagerModalOpen && (
        <CommTemplateManagerModal
          onClose={() => form.setIsTemplateManagerModalOpen(false)}
        />
      )}
    </div>
  );
};

export default ConfigViewController;
